import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, Inbox, CheckCircle2, Circle, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";

const fmtDate = (iso) =>
    new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const RequestCard = ({ req, index, onChange, onDelete }) => {
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const toggle = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/requests/${req.id}`, { handled: !req.handled });
            onChange({ ...req, handled: data.handled });
        } catch (err) {
            toast.error(apiError(err, "Aggiornamento non riuscito."));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/requests/${req.id}`);
            onDelete(req.id);
            toast.success("Richiesta eliminata.");
        } catch (err) {
            toast.error(apiError(err, "Eliminazione non riuscita."));
            setBusy(false);
        }
    };

    return (
        <article
            data-testid={`admin-request-${index}`}
            className={`border p-5 transition-colors ${req.handled ? "border-white/10 bg-white/[0.02] opacity-70" : "border-tl-orange/40 bg-tl-dark"}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p data-testid={`admin-request-name-${index}`} className="font-display text-lg font-semibold text-white">{req.nome}</p>
                    {req.azienda && (
                        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-tl-mutedLight">
                            <Building2 size={13} /> {req.azienda}
                        </p>
                    )}
                </div>
                <div className="text-right">
                    <p data-testid={`admin-request-date-${index}`} className="font-mono-tl text-[10px] uppercase tracking-[0.2em] text-white/50">{fmtDate(req.created_at)}</p>
                    {req.email_sent === false && (
                        <p className="mt-1 flex items-center justify-end gap-1 font-mono-tl text-[10px] uppercase tracking-[0.15em] text-amber-400">
                            <AlertTriangle size={11} /> Email non inviata
                        </p>
                    )}
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <a data-testid={`admin-request-email-${index}`} href={`mailto:${req.email}`} className="flex items-center gap-1.5 text-tl-orange hover:underline">
                    <Mail size={13} /> {req.email}
                </a>
                {req.telefono && (
                    <a href={`tel:${req.telefono.replace(/\s+/g, "")}`} className="flex items-center gap-1.5 text-white/80 hover:text-tl-orange">
                        <Phone size={13} /> {req.telefono}
                    </a>
                )}
            </div>
            <p data-testid={`admin-request-message-${index}`} className="mt-4 whitespace-pre-wrap border-l-2 border-white/10 pl-4 text-sm leading-relaxed text-white/85">
                {req.messaggio}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    data-testid={`admin-request-toggle-${index}`}
                    onClick={toggle}
                    disabled={busy}
                    className={`inline-flex items-center gap-2 border px-3 py-2 font-mono-tl text-[10px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50 ${
                        req.handled ? "border-white/20 text-white/60 hover:border-white/50" : "border-tl-orange text-tl-orange hover:bg-tl-orange hover:text-tl-dark"
                    }`}
                >
                    {req.handled ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                    {req.handled ? "Gestita" : "Segna come gestita"}
                </button>
                {confirm ? (
                    <span className="flex items-center gap-1">
                        <button type="button" data-testid={`admin-request-confirm-delete-${index}`} onClick={remove} disabled={busy} className="bg-red-500 px-2.5 py-1.5 font-mono-tl text-[10px] uppercase tracking-[0.15em] text-white hover:bg-red-600 disabled:opacity-60">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : "Conferma"}
                        </button>
                        <button type="button" onClick={() => setConfirm(false)} className="border border-white/30 px-2.5 py-1.5 font-mono-tl text-[10px] uppercase tracking-[0.15em] text-white">No</button>
                    </span>
                ) : (
                    <button type="button" data-testid={`admin-request-delete-${index}`} onClick={() => setConfirm(true)} aria-label="Elimina richiesta" className="grid h-8 w-8 place-items-center border border-white/20 text-white/60 transition-colors hover:border-red-400 hover:text-red-400">
                        <Trash2 size={13} />
                    </button>
                )}
            </div>
        </article>
    );
};

export const RequestsPanel = () => {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("open");

    useEffect(() => {
        api.get("/admin/requests")
            .then(({ data }) => setItems(data.items))
            .catch(() => { toast.error("Impossibile caricare le richieste."); setItems([]); });
    }, []);

    if (items === null) return <p className="text-sm text-tl-mutedLight">Caricamento richieste…</p>;

    const open = items.filter((r) => !r.handled);
    const shown = filter === "open" ? open : filter === "handled" ? items.filter((r) => r.handled) : items;

    return (
        <div data-testid="admin-requests-panel">
            <div className="flex flex-wrap items-center gap-2">
                {[["open", `Da gestire (${open.length})`], ["handled", `Gestite (${items.length - open.length})`], ["all", `Tutte (${items.length})`]].map(([k, label]) => (
                    <button
                        key={k}
                        type="button"
                        data-testid={`admin-requests-filter-${k}`}
                        onClick={() => setFilter(k)}
                        className={`border px-4 py-2 font-mono-tl text-[10px] uppercase tracking-[0.2em] transition-colors ${
                            filter === k ? "border-tl-orange bg-tl-orange text-tl-dark" : "border-white/15 text-white/70 hover:border-white/40"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {shown.length === 0 ? (
                <div data-testid="admin-requests-empty" className="mt-6 border border-white/10 p-12 text-center">
                    <Inbox size={28} className="mx-auto text-tl-orange" strokeWidth={1.5} />
                    <p className="mt-4 text-sm text-tl-mutedLight">Nessuna richiesta in questa vista.</p>
                </div>
            ) : (
                <div className="mt-6 grid gap-4 lg:grid-cols-2">
                    {shown.map((r, i) => (
                        <RequestCard
                            key={r.id}
                            req={r}
                            index={i}
                            onChange={(upd) => setItems((prev) => prev.map((x) => (x.id === upd.id ? upd : x)))}
                            onDelete={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
